import { zodResolver } from "@hookform/resolvers/zod";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { Button, Container, ErrorMessage, Loading, Panel, Typography } from "@/components/atoms";
import { PhoneNumberInput } from "@/components/atoms/PhoneNumber/PhoneNumberInput";
import { GeneralFooter, GeneralHeader } from "@/components/organisms";
import { GeneralLogo } from "@/components/organisms/General/GeneralLogo";
import { Expired } from "@/components/pages";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useSiteConfig } from "@/hooks";
import { useVerifyProviderTokenQuery, useLazyGetProviderQuery } from "@/redux/apis/auth/authApi";
import { useRegisterUserAccountMutation } from "@/redux/apis/auth/authApi";
import { useUpdateProfileMutation } from "@/redux/apis/profile/profileApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setActiveOperator } from "@/redux/slices/operator/operatorSlice";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";
import { addToast } from "@/utils";

const formSchema = z.object({
  mobile: z
    .string()
    .min(1, "Mobile number is required")
    .refine((value) => isValidPhoneNumber(value), { message: "Please enter a valid mobile number" }),
});

type FormData = z.infer<typeof formSchema>;

/**
 * UserAccountOnboarding - Handles the post-auth registration for user accounts (Admin, Manager, Dispatcher, Call Center)
 *
 * This is the second phase of the invite flow, after Kinde authentication.
 *
 * Flow:
 * 1. User arrives here after Kinde auth (redirected from AuthCallback)
 * 2. Verify the invite key is still valid
 * 3. Show form to collect mobile number
 * 4. Call POST /v3/auth/register/user/{inviteKey} with mobile
 * 5. On success → redirect to dashboard
 */
export const UserAccountOnboarding = () => {
  const { inviteKey } = useParams<{ inviteKey: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { apiUrl } = useSiteConfig();

  // Verify the invite token is still valid
  const {
    isLoading: isVerifying,
    isSuccess: isValidToken,
    data: tokenData,
    isError: isVerifyError,
  } = useVerifyProviderTokenQuery(inviteKey || "", {
    skip: !inviteKey,
  });

  const [getProvider] = useLazyGetProviderQuery();
  const [registerUserAccount, { isLoading: isRegistering }] = useRegisterUserAccountMutation();
  const [updateProfile, { isLoading: isUpdatingProfile }] = useUpdateProfileMutation();
  const operator = useAppSelector(activeOperatorSelector);

  const isSubmitting = isRegistering || isUpdatingProfile;

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      mobile: "",
    },
  });

  // Set active operator when token is verified
  useEffect(() => {
    if (isValidToken && tokenData?.provider_uuid) {
      getProvider(tokenData.provider_uuid)
        .unwrap()
        .then((operator) => {
          dispatch(setActiveOperator(operator));
        })
        .catch((error) => {
          console.error("Failed to fetch operator:", error);
        });
    }
  }, [isValidToken, tokenData, getProvider, dispatch]);

  // Token verification failed - redirect to dashboard
  // The token may be expired/consumed because the user already completed registration
  // Let the dashboard/auth flow handle routing the user appropriately
  useEffect(() => {
    if (isVerifyError) {
      navigate("/", { replace: true });
    }
  }, [isVerifyError, navigate]);

  const onSubmit = handleSubmit(async (data) => {
    if (!inviteKey) {
      addToast("danger", "Invite key not found. Please try again.");
      return;
    }

    try {
      await registerUserAccount({
        apiUrl,
        inviteKey,
        mobile: data.mobile,
      }).unwrap();

      // Mark registration as complete (same pattern as operator/client flows)
      await updateProfile({
        account_metadata: {
          is_register_complete: true,
          provider_code: operator?.providerCode,
          type: "user_account",
        },
      }).unwrap();

      addToast("success", "Welcome! Your account has been set up successfully.");
      navigate("/auth-callback", { replace: true });
    } catch (error) {
      console.error("Failed to register user account:", error);
      getErrorMessages(error as FetchBaseQueryError | SerializedError).forEach((m) => addToast("danger", m));
    }
  });

  // No invite key in URL
  if (!inviteKey) {
    return (
      <Expired
        title="Invitation Expired"
        description="Sorry, the invitation link is no longer valid. Please contact the person who invited you."
      />
    );
  }

  // Token verification failed - show loading while redirecting
  if (isVerifyError) {
    return <Loading />;
  }

  // Show loading while verifying token
  if (isVerifying || !isValidToken) {
    return <Loading />;
  }

  return (
    <div className="flex min-h-screen flex-row overflow-hidden">
      <div className="relative flex flex-1 flex-col">
        <GeneralHeader action="logout" />
        <Container className="relative flex w-full max-w-[1040px] flex-1 items-start justify-center gap-5 px-5 py-8">
          <div className="flex w-full justify-center">
            <Panel className="relative h-fit w-[440px] p-8">
              {isSubmitting && <Loading className="rounded-lg" />}

              {/* Logo and Brand */}
              <div className="mb-8">
                <GeneralLogo />
              </div>

              {/* Title and Description */}
              <div className="mb-6">
                <Typography variant="h1" className="mb-2">
                  Complete your profile
                </Typography>
                <Typography variant="paragraph" className="text-neutral-dark-gray">
                  Please enter your mobile number to complete your account setup.
                </Typography>
              </div>

              {/* Form */}
              <form onSubmit={onSubmit} className="flex flex-col gap-6">
                {/* Mobile Number */}
                <div className="flex flex-col gap-1">
                  <Typography variant="action" className="text-neutral-dark-gray">
                    Mobile Number
                  </Typography>
                  <PhoneNumberInput control={control} name="mobile" hasError={!!formErrors.mobile} />
                  <ErrorMessage errors={formErrors} name="mobile" />
                </div>

                {/* Submit Button */}
                <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isSubmitting}>
                  Complete Setup
                </Button>
              </form>
            </Panel>
          </div>
        </Container>
        <GeneralFooter />
      </div>
    </div>
  );
};
